import { useEffect, useState } from 'react';
import { ResolveDisputeRequest } from '../../services/ordersApi';
import { FAULT_PARTY_LABELS, FaultParty, FeeBorneBy, serviceCeilingOf } from '../../types/refunds';
import RefundConsequence, { useRefundPreview } from './RefundConsequence';
import DeliveryFeeRefundCheckbox from './DeliveryFeeRefundCheckbox';
import ServiceFeeRefundCheckbox, { serviceFeeRefundChecked } from './ServiceFeeRefundCheckbox';

const FAULT_PARTIES: FaultParty[] = ['GARAGE', 'PLATFORM', 'CUSTOMER', 'NONE'];

/** BR-25's message, rendered client-side before the round trip and echoed by billing's 422. */
const OVER_CEILING_MESSAGE = 'Refund exceeds the amount paid';

/** Nothing is left on either slice — the shortcut resolved to 0.00 and the fee is not being returned. */
const NOTHING_TO_REFUND_MESSAGE = 'Nothing is left to refund on this payment';

/** Spec 068 review — the preview for the current choices has not answered yet. */
const PREVIEW_PENDING_MESSAGE = 'Calculating the refund preview…';

interface ResolveDisputeModalProps {
  onSubmit: (request: ResolveDisputeRequest) => Promise<void>;
  onClose: () => void;
  /** Spec 053 AC-7 — when the order is paid, the consequence line previews the chosen refund. */
  paymentId?: string | null;
  /**
   * The garage's service gross (`order.totalAmount`, which carries no delivery fee — see
   * `serviceGrossOf`). This IS the basis for the refund amount; Garagat's own fee is refunded only
   * by the explicit checkbox below.
   */
  totalAmount?: number;
  /** Spec 053 Phase 3 — Garagat's fee on this order, VAT-inclusive. 0/absent ⇒ no fee. */
  deliveryFeeGross?: number | null;
  deliveryTrips?: number | null;
  currency?: string;
  garageName?: string | null;
  token?: string;
}

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

/**
 * What a percent shortcut puts in the amount field: that percentage of the service the garage
 * supplied, never above what is still refundable. Before the preview lands the cap IS the service
 * gross, which is what the dialog assumed unconditionally until #595 — and why 100 % of an order
 * whose gateway fee is withheld produced an amount the dialog then refused to submit.
 *
 * <p>At 100 % the figure is {@code payable} — the service slice billing itself computed for the
 * SENTINEL (`amountRequested` on a preview requested with no amount), not the service ceiling. The
 * two differ on a fee-bearing order: `RefundSplit` fills the delivery slice first, so the service
 * slice is `min(serviceCeiling, paymentCeiling − deliveryFee)` and the ceiling overstates it by the
 * gateway fee. Showing the ceiling there put a figure in the field that disagreed with the
 * consequence line one row below it — and that, had it been typed, billing answers 422 to.
 */
function shortcutAmount(serviceGross: number, ceiling: number | null, payable: number | null, pct: number): string {
  if (pct >= 100 && payable != null) {
    return round2(payable).toFixed(2);
  }
  return round2(Math.min((serviceGross * pct) / 100, ceiling ?? serviceGross)).toFixed(2);
}

/**
 * Spec 048 dispute resolution, extended by Spec 053 Flow F5 / AC-40.
 *
 * <p>The refund is entered as a **custom AED amount against the service slice**; the percentage
 * slider is only a shortcut that fills it (Flow F5), and the amount — not the percentage — is what
 * the backend acts on, so a typed 100.00 refunds 100.00 and not the nearest whole percent of the
 * service. Refunding Garagat's own pickup & delivery fee is a separate, explicit decision (the
 * checkbox), because it is Garagat's supply and not the garage's.
 *
 * <p>The one exception is 100 %, which is an *intent* rather than an amount (#595): it submits no
 * amount at all so billing applies its own split, and the field shows the service slice that split
 * produces — `preview.amountRequested`, NOT the service ceiling, because on a fee-bearing order the
 * delivery slice is filled first and the ceiling overstates the service slice by the gateway fee. A
 * shortcut stays in force and re-derives its figure whenever that number moves: when the preview
 * first lands, and again whenever a fee checkbox changes what billing would withhold.
 */
export default function ResolveDisputeModal({
  onSubmit,
  onClose,
  paymentId,
  totalAmount,
  deliveryFeeGross,
  deliveryTrips,
  currency,
  garageName,
  token,
}: ResolveDisputeModalProps) {
  const [outcome, setOutcome] = useState<'CLOSE' | 'RETURN_TO_SERVICE'>('CLOSE');
  const [refundPercent, setRefundPercent] = useState(0);
  const [refundAmount, setRefundAmount] = useState('');
  /**
   * The percentage currently *driving* the amount field, or null once the admin types their own
   * figure. It is what makes "100 %" an intent rather than a number: the field holds the payable,
   * so a clamped 190.76 no longer looks like "all of the 200.00 service" to any test of the number
   * alone.
   */
  const [shortcutPercent, setShortcutPercent] = useState<number | null>(null);
  const [faultParty, setFaultParty] = useState<FaultParty | null>(null);
  // #1059 — undefined = follow billing's default (Garagat absorbs the gateway fee on its own fault).
  const [platformBearsFee, setPlatformBearsFee] = useState<boolean | undefined>(undefined);
  const [refundDeliveryFee, setRefundDeliveryFee] = useState(false);
  // Spec 068 AC-18 — undefined = follow billing's default for the fault party.
  const [refundServiceFee, setRefundServiceFee] = useState<boolean | undefined>(undefined);
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The service slice is what a dispute refund is measured against (delta D27). `totalAmount` is
  // already that slice — orders.total_amount never carried Garagat's fee — so subtracting the fee
  // here would understate the ceiling by exactly the fee and under-refund a full service refund.
  const serviceGross = totalAmount != null ? round2(totalAmount) : null;
  const parsedAmount = parseFloat(refundAmount);
  const hasAmount = !Number.isNaN(parsedAmount) && parsedAmount > 0;
  // Mirrors `ResolveDisputeRequest.isRefunding()` on the orders side — the server decides a fault
  // party is REQUIRED from the percentage too, and the percentage travels in the request whether or
  // not the field it filled came out as a number (#595). Keying on the amount alone meant a service
  // ceiling of 0.00 clamped the field to "0.00", unmounted the fault-party radios and ENABLED
  // Confirm, and the server then answered 400 naming a control the dialog had just taken away.
  const refunding = outcome === 'CLOSE' && (hasAmount || refundDeliveryFee || refundPercent > 0);
  const feeBorneBy: FeeBorneBy | undefined =
    platformBearsFee === undefined ? undefined : platformBearsFee ? 'PLATFORM' : 'CUSTOMER';

  // A 100% service refund still goes over as "no amount" so billing refunds the service ceiling,
  // exactly as it did before a custom amount existed. `fullIntent` is that decision held
  // EXPLICITLY (#595): `AdminOrderService.disputeServiceAmount` maps `refundPercent >= 100` with no
  // amount to `null`, i.e. "billing, use your own ceiling", and that is the only shape in which a
  // delivery-fee-ticked full refund passes billing's payment-wide check. Inferring it from
  // `parsedAmount >= serviceGross` stopped working the moment the field was clamped to the ceiling.
  const fullIntent = shortcutPercent === 100;
  const fullService = serviceGross != null && hasAmount && parsedAmount >= serviceGross;
  const previewAmount = !hasAmount || fullIntent || fullService ? null : round2(parsedAmount);

  const { preview, loading: previewLoading, error: previewError, pending: previewPending } = useRefundPreview({
    paymentId,
    faultParty,
    amount: previewAmount,
    enabled: refunding,
    feeBorneBy,
    refundDeliveryFee,
    refundServiceFee,
    // #716 — a CLOSE completes the order first, then refunds with the fault honoured (#621).
    closing: outcome === 'CLOSE',
    token: token ?? '',
  });

  // Spec 068 — the checkbox SHOWS the admin's choice, else the preview's policy default; only an
  // explicit choice is ever SENT (design §3.6). The fault-party handler drops the explicit choice.
  const serviceFeeCharged = preview?.serviceFeeCharged ?? null;
  const serviceFeeTicked = (serviceFeeCharged ?? 0) > 0 && serviceFeeRefundChecked(refundServiceFee, preview);
  const selectFaultParty = (party: FaultParty) => {
    setFaultParty(party);
    setRefundServiceFee(undefined);
    setPlatformBearsFee(undefined);
  };
  // #1059 — the processing-fee box SHOWS the admin's choice, else the bearer billing's preview resolved.
  const platformFeeTicked = platformBearsFee ?? preview?.feeBorneBy === 'PLATFORM';
  // A preview due for the current inputs that has not answered: nothing it shows may be acted on.
  const awaitingPreview = refunding && !!faultParty && !!previewPending;

  // AC-40: the ceiling is the SERVICE ceiling the preview returns, not the payment total.
  const serviceCeiling = serviceCeilingOf(preview, serviceGross);
  // What billing will actually pay on the service slice under the sentinel. Only meaningful while
  // `fullIntent` holds: that is the one case in which the preview was requested with NO amount, so
  // `amountRequested` is billing's own computation and not an echo of the figure we sent it.
  const fullServicePayable = fullIntent && preview?.amountRequested != null
    ? round2(preview.amountRequested)
    : null;
  // The guard is about a TYPED amount. Under `fullIntent` no amount is submitted at all, so there
  // is nothing that could exceed the ceiling — and the field legitimately holds the pre-preview
  // service gross for the render between the shortcut being applied and the ceiling arriving.
  const overCeiling = hasAmount && !fullIntent && serviceCeiling != null && parsedAmount > serviceCeiling + 0.0001;

  // The fee is known from the order; the preview is the fallback for an order payload that
  // predates the field, so the checkbox can never be silently unreachable.
  const feeCharged = deliveryFeeGross ?? preview?.deliveryFeeCharged ?? null;

  const applyPercent = (pct: number) => {
    setRefundPercent(pct);
    setShortcutPercent(pct === 0 ? null : pct);
    if (serviceGross == null) return;
    setRefundAmount(pct === 0 ? '' : shortcutAmount(serviceGross, serviceCeiling, fullServicePayable, pct));
  };

  const handleAmountChange = (value: string) => {
    setRefundAmount(value);
    // The admin is driving the field now — no shortcut may overwrite what they typed, and a typed
    // figure is never the "let billing pick" sentinel however large it is.
    setShortcutPercent(null);
    const amt = parseFloat(value);
    if (serviceGross != null && serviceGross > 0 && !Number.isNaN(amt)) {
      setRefundPercent(Math.max(0, Math.min(100, Math.round((amt / serviceGross) * 100))));
    } else {
      // Cleared OR unparseable. `refundPercent` is sent on every CLOSE, so leaving the last figure
      // standing behind an empty field would refund a percentage the admin can no longer see.
      setRefundPercent(0);
    }
  };

  // The ceiling is not known on the first click of a shortcut — the preview needs a fault party and
  // a round trip, and it MOVES afterwards whenever the fee checkboxes change what billing would
  // withhold. So a shortcut stays in force and re-derives its figure here, one render after the new
  // ceiling lands. `shortcutPercent` is cleared by `handleAmountChange`, so this can never
  // overwrite a typed amount.
  useEffect(() => {
    if (shortcutPercent == null || serviceGross == null) return;
    const next = shortcutAmount(serviceGross, serviceCeiling, fullServicePayable, shortcutPercent);
    setRefundAmount((current) => (current === next ? current : next));
  }, [shortcutPercent, serviceCeiling, fullServicePayable, serviceGross]);

  /**
   * Why Confirm is disabled, in the order `handleSubmit` checks. One string, so the button's
   * tooltip and the line beside it can never disagree — before #595 the only disabled state that
   * said anything was the fault party, and only as a `title` no one hovers.
   */
  /**
   * A refund is intended but neither slice can carry it: the service ceiling has gone to zero (an
   * earlier refund already took it) and the fee is not being returned either. Submitting would send
   * billing a refund of 0.00. Stated here rather than left for the gateway to reject.
   */
  // The fee slice counts only from a settled preview (or an explicit tick) — never a stale one.
  const serviceFeeRefunding = refundServiceFee ?? (!previewPending && serviceFeeTicked);
  const nothingToRefund = refunding && !awaitingPreview && !hasAmount && !refundDeliveryFee && !serviceFeeRefunding;

  const disabledReason = !message.trim()
    ? 'A resolution message is required.'
    : nothingToRefund
      ? NOTHING_TO_REFUND_MESSAGE
      : refunding && !faultParty
        ? 'Select who is responsible for this refund'
        : awaitingPreview
          ? PREVIEW_PENDING_MESSAGE
          : overCeiling
            ? OVER_CEILING_MESSAGE
            : null;

  const handleSubmit = async () => {
    setError(null);
    if (!message.trim()) {
      setError('A resolution message is required.');
      return;
    }
    if (nothingToRefund) {
      setError(NOTHING_TO_REFUND_MESSAGE);
      return;
    }
    if (refunding && !faultParty) {
      setError('Select who is responsible for this refund');
      return;
    }
    if (awaitingPreview) {
      setError(PREVIEW_PENDING_MESSAGE);
      return;
    }
    if (overCeiling) {
      setError(OVER_CEILING_MESSAGE);
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit({
        outcome,
        refundPercent: outcome === 'CLOSE' ? refundPercent : undefined,
        refundAmount: refunding && previewAmount != null ? previewAmount : undefined,
        refundDeliveryFee: refunding && refundDeliveryFee ? true : undefined,
        // Spec 068 — what the checkbox shows once the preview stated a fee; else billing's default.
        refundServiceFee: refunding ? refundServiceFee : undefined,
        message: message.trim(),
        faultParty: refunding && faultParty ? faultParty : undefined,
        feeBorneBy: refunding ? feeBorneBy : undefined,
      });
    } catch (err) {
      // Keep the modal open so the admin can retry (e.g. 502 from billing). A 422 carries BR-25's
      // message in the body and ApiError surfaces it verbatim — it renders here, inline.
      setError(err instanceof Error ? err.message : 'Failed to resolve dispute.');
      setSubmitting(false);
    }
  };

  return (
    <div className="admin-modal-overlay">
      <div className="admin-modal" style={{ maxWidth: '480px', width: '100%' }} onClick={(e) => e.stopPropagation()}>
        <div className="admin-modal-header">
          <h3 style={{ margin: 0 }}>Resolve Dispute</h3>
          <button className="admin-modal-close" onClick={onClose} disabled={submitting}>&times;</button>
        </div>

        <div className="admin-modal-body">
          <div className="admin-form-group">
            <label className="admin-form-label">Outcome</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input
                  type="radio"
                  checked={outcome === 'CLOSE'}
                  onChange={() => setOutcome('CLOSE')}
                  disabled={submitting}
                />
                Close order (optionally refund the customer)
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input
                  type="radio"
                  checked={outcome === 'RETURN_TO_SERVICE'}
                  onChange={() => setOutcome('RETURN_TO_SERVICE')}
                  disabled={submitting}
                />
                Return to service (resume the order journey)
              </label>
            </div>
          </div>

          {outcome === 'CLOSE' && (
            <>
              <div className="admin-form-group">
                <label className="admin-form-label" htmlFor="dispute-refund-amount">
                  Service refund {currency ? `(${currency})` : ''}
                </label>
                <input
                  id="dispute-refund-amount"
                  type="number"
                  className="admin-form-input"
                  value={refundAmount}
                  onChange={(e) => handleAmountChange(e.target.value)}
                  min={0}
                  step={0.01}
                  disabled={submitting}
                  placeholder="0.00"
                  aria-invalid={overCeiling || undefined}
                  aria-describedby="dispute-refund-amount-help"
                />
                <p
                  id="dispute-refund-amount-help"
                  style={{ fontSize: '0.8125rem', color: 'var(--admin-text-muted)', margin: '0.25rem 0 0' }}
                >
                  {serviceCeiling != null ? (
                    <>
                      Up to{' '}
                      <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                        {currency ?? 'AED'} {serviceCeiling.toFixed(2)}
                      </span>{' '}
                      — the service the garage supplied
                      {(feeCharged ?? 0) > 0 ? ', excluding the pickup & delivery fee' : ''}.
                    </>
                  ) : (
                    'The service the garage supplied.'
                  )}
                </p>
              </div>

              <div className="admin-form-group">
                <label className="admin-form-label">Percent shortcut: {refundPercent}%</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <input
                    type="range"
                    aria-label="Percent shortcut"
                    min={0}
                    max={100}
                    step={1}
                    value={refundPercent}
                    onChange={(e) => applyPercent(Number(e.target.value))}
                    disabled={submitting || serviceGross == null}
                    style={{ flex: 1 }}
                  />
                  <input
                    type="number"
                    className="admin-form-input"
                    aria-label="Percent"
                    min={0}
                    max={100}
                    step={1}
                    value={refundPercent}
                    onChange={(e) => {
                      const v = Math.max(0, Math.min(100, Number(e.target.value)));
                      applyPercent(Number.isNaN(v) ? 0 : v);
                    }}
                    disabled={submitting || serviceGross == null}
                    style={{ width: '80px' }}
                  />
                </div>
              </div>

              <DeliveryFeeRefundCheckbox
                feeCharged={feeCharged}
                trips={deliveryTrips}
                currency={currency ?? 'AED'}
                checked={refundDeliveryFee}
                onChange={setRefundDeliveryFee}
                disabled={submitting}
              />
            </>
          )}

          {refunding && (
            <>
              <div className="admin-form-group" role="radiogroup" aria-labelledby="dispute-fault-party-label">
                <label className="admin-form-label" id="dispute-fault-party-label">
                  Who is responsible? <span style={{ color: 'var(--admin-danger)' }}>*</span>
                </label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
                  {FAULT_PARTIES.map((party) => (
                    <label key={party} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                      <input
                        type="radio"
                        name="dispute-fault-party"
                        value={party}
                        checked={faultParty === party}
                        onChange={() => selectFaultParty(party)}
                        disabled={submitting}
                      />
                      {FAULT_PARTY_LABELS[party]}
                    </label>
                  ))}
                </div>
              </div>

              {/* Spec 068 V11 — appears once the preview (which needs the fault party) states a fee > 0. */}
              <ServiceFeeRefundCheckbox
                feeCharged={serviceFeeCharged}
                currency={currency ?? 'AED'}
                checked={serviceFeeTicked}
                onChange={setRefundServiceFee}
                disabled={submitting}
                borneBy={preview?.serviceFeeBorneBy ?? null}
              />

              <div className="admin-form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={platformFeeTicked}
                    onChange={(e) => setPlatformBearsFee(e.target.checked)}
                    disabled={submitting}
                  />
                  Refund the payment processing fee too (Garagat absorbs it)
                </label>
              </div>

              {paymentId && currency && token && (
                <div className="admin-form-group">
                  <RefundConsequence
                    preview={preview}
                    loading={previewLoading}
                    error={previewError}
                    faultParty={faultParty}
                    currency={currency}
                    garageName={garageName ?? null}
                  />
                </div>
              )}
            </>
          )}

          <div className="admin-form-group">
            <label className="admin-form-label">
              Resolution Message <span style={{ color: 'var(--admin-danger)' }}>*</span>
            </label>
            <textarea
              className="admin-textarea"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              placeholder="Explain how this dispute was resolved…"
              disabled={submitting}
            />
          </div>

          {overCeiling && !error && (
            <div className="admin-info-banner danger" role="alert">{OVER_CEILING_MESSAGE}</div>
          )}
          {error && <div className="admin-info-banner danger" role="alert">{error}</div>}
        </div>

        <div className="admin-modal-footer">
          {/* The over-ceiling case already has its own danger banner above; repeating it here would
              put the same sentence on the screen twice. Everything else was invisible until now. */}
          {disabledReason && disabledReason !== OVER_CEILING_MESSAGE && (
            <span
              data-testid="resolve-disabled-reason"
              style={{ marginRight: 'auto', fontSize: '0.8125rem', color: 'var(--admin-text-muted)' }}
            >
              {disabledReason}
            </span>
          )}
          <button className="admin-btn admin-btn-outline" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button
            className="admin-btn admin-btn-primary"
            onClick={handleSubmit}
            disabled={submitting || disabledReason != null}
            title={disabledReason ?? undefined}
          >
            {submitting ? 'Resolving…' : 'Confirm Resolution'}
          </button>
        </div>
      </div>
    </div>
  );
}
